import React, { memo } from "react";

import GridColumn from "../../components/grid/GridColumn";
import GridBodyRow from "../../components/grid/GridBodyRow";
import GridGrouping from "./GridGrouping";
import PageSpecification from "../../types/system/PageSpecification";
import GridBodyGroupRow from "./GridBodyGroupRow";
import Utilities from "../../foundation/Utility";

interface GridBodyProps<T, U = T> {
    columns: GridColumn<U>[];
    grouping?: GridGrouping<T, U>;
    records: T[];

    draggable?: boolean;
    dragContainerRef?: React.RefObject<HTMLTableSectionElement>;
    globalSelect: boolean;
    keyField: string;
    limitTo?: number;
    pageSpecification: PageSpecification;
    selectedColumn: GridColumn<U>;
    selectedRecord: T | U;
    selectedRecords: (T | U)[];
    sortField: string;
    filters: Map<string, any>;
    bulkEdit?: boolean;

    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    onFocus?: (record: T | U, column: GridColumn<T | U>) => void;
    onBlur?: (record: T | U, column: GridColumn<T | U>) => void;
    onCheck?: (record: T, value: boolean) => void;
    onClick?: (record: T | U, column: GridColumn<T | U>) => void;
    onDoubleClick?: (record: T | U, column: GridColumn<T | U>) => void;
    onDragStart: (
        record: T | U,
        column: GridColumn<T | U>,
        event: React.MouseEvent,
        cell: React.RefObject<HTMLTableCellElement>,
    ) => void;
    valueEdited?: (
        record: T | U,
        column: GridColumn<T | U>,
        value: any,
    ) => void;
    prevRecordRequested?: () => void;
    nextRecordRequested?: () => void;
    prevColumnRequested?: () => void;
    nextColumnRequested?: () => void;
}

interface GridBodyState {
    observer?: MutationObserver;
    rect?: DOMRect;
}

class GridBody<T, U = T> extends React.Component<
    GridBodyProps<T, U>,
    GridBodyState
> {
    state: GridBodyState = {};

    hasFilters() {
        return this.props.columns.some(column => !!column.filterable);
    }

    rowSelected(record: T | U) {
        return (
            this.props.selectedRecords &&
            !!this.props.selectedRecords.find(
                item =>
                    item[this.props.keyField] === record[this.props.keyField],
            )
        );
    }

    rowSingleSelected(record: T | U) {
        return (
            this.props.selectedRecord &&
            this.props.selectedRecord[this.props.keyField] ===
                record[this.props.keyField]
        );
    }

    getGroupColumns(): GridColumn<T>[] {
        if (!this.props.grouping) return null;

        return (
            (this.props.grouping.children instanceof Array
                ? this.props.grouping.children
                : [this.props.grouping.children]) as React.ReactElement<
                GridColumn<T>
            >[]
        ).map(entry => entry.props as GridColumn<T>);
    }

    getGroupVisible(value: U[]): U[] {
        let visible = value ?? [];
        if (this.props.limitTo)
            visible = visible.slice(undefined, this.props.limitTo);
        else visible = visible.slice();
        if (this.props.sortField)
            visible = visible.sort(Utilities.getSorter([this.props.sortField]));
        if (this.props.filters && this.props.filters.size > 0) {
            const filterer = Utilities.getFilterer(this.props.filters);
            visible = visible.filter(filterer);
        }

        return visible;
    }

    render(): React.ReactNode {
        return (this.props.grouping ? this.props.records : [{} as T]).map(
            (group, groupIndex) => (
                <tbody key={groupIndex}>
                    {this.props.grouping && (
                        <GridBodyGroupRow
                            key={groupIndex + 1}
                            index={groupIndex}
                            record={group}
                            columns={this.getGroupColumns()}
                            dragContainerRef={this.props.dragContainerRef}
                            draggable={this.props.draggable}
                            selected={this.rowSelected(group)}
                            globalSelect={this.props.globalSelect}
                            singleSelected={this.rowSingleSelected(group)}
                            selectedColumn={this.props.selectedColumn}
                            pageSpecification={this.props.pageSpecification}
                            onKeyDown={this.props.onKeyDown}
                            onKeyUp={this.props.onKeyUp}
                            onFocus={this.props.onFocus}
                            onBlur={this.props.onBlur}
                            onClick={this.props.onClick}
                            onDoubleClick={this.props.onDoubleClick}
                            prevRecordRequested={this.props.prevRecordRequested}
                            nextRecordRequested={this.props.nextRecordRequested}
                            prevColumnRequested={this.props.prevColumnRequested}
                            nextColumnRequested={this.props.nextColumnRequested}
                        />
                    )}
                    {(
                        (this.props.grouping
                            ? this.getGroupVisible(
                                  group[this.props.grouping.value] as U[],
                              )
                            : this.props.records) ?? []
                    ).map((record: T | U, rowIndex) => (
                        <GridBodyRow
                            key={rowIndex + groupIndex * 100000 + 1}
                            index={rowIndex}
                            record={record}
                            columns={this.props.columns}
                            dragContainerRef={this.props.dragContainerRef}
                            draggable={this.props.draggable}
                            selected={this.rowSelected(record)}
                            globalSelect={this.props.globalSelect}
                            singleSelected={this.rowSingleSelected(record)}
                            selectedColumn={this.props.selectedColumn}
                            pageSpecification={this.props.pageSpecification}
                            onKeyDown={this.props.onKeyDown}
                            onKeyUp={this.props.onKeyUp}
                            onFocus={this.props.onFocus}
                            onBlur={this.props.onBlur}
                            onCheck={this.props.onCheck}
                            onClick={this.props.onClick}
                            onDoubleClick={this.props.onDoubleClick}
                            onDragStart={this.props.onDragStart}
                            valueEdited={this.props.valueEdited}
                            prevRecordRequested={this.props.prevRecordRequested}
                            nextRecordRequested={this.props.nextRecordRequested}
                            prevColumnRequested={this.props.prevColumnRequested}
                            nextColumnRequested={this.props.nextColumnRequested}
                            bulkEdit={this.props.bulkEdit}
                        />
                    ))}
                </tbody>
            ),
        );
    }
}

export default memo(GridBody) as unknown as typeof GridBody;
